import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import './AttendanceDashboard.css';

const AttendanceView = () => {
  const { attendance, users, exportAttendance } = useData();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Get all employees (filter out non-employee roles if needed)
  const allEmployees = users.filter(user => 
    ['employee', 'project_manager', 'hr'].includes(user.role)
  );

  // Filter attendance by selected date
  const dateAttendance = attendance.filter(record => record.date === selectedDate);

  // Create comprehensive employee attendance data
  const getEmployeeAttendanceData = () => {
    return allEmployees.map(employee => {
      const attendanceRecord = dateAttendance.find(record => record.userId === employee.id);

      if (attendanceRecord) {
        // Employee has attendance record
        return {
          ...attendanceRecord,
          employee: employee,
          isPresent: !!attendanceRecord.checkIn,
          status: attendanceRecord.status || 'present'
        };
      } else {
        // Employee is absent (no attendance record)
        return {
          id: `absent-${employee.id}`,
          userId: employee.id,
          userName: employee.name,
          date: selectedDate,
          checkIn: null,
          checkOut: null,
          hoursWorked: 0,
          status: 'absent',
          employee: employee,
          isPresent: false
        };
      }
    });
  };

  // Get sorted employee data (present first, then absent)
  const employeeAttendanceData = getEmployeeAttendanceData().sort((a, b) => {
    // Present employees first
    if (a.isPresent && !b.isPresent) return -1;
    if (!a.isPresent && b.isPresent) return 1;

    // Within same status, sort by name
    return a.userName.localeCompare(b.userName);
  });

  // Date navigation functions
  const navigateDate = (direction) => {
    const currentDate = new Date(selectedDate);
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (direction === 'next') {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setShowDatePicker(false);
  };

  const getStatusInfo = (status) => {
    const statusConfig = {
      present: { class: 'att-status-present', icon: 'fas fa-check-circle', text: 'Present' },
      absent: { class: 'att-status-absent', icon: 'fas fa-times-circle', text: 'Absent' },
      late: { class: 'att-status-late', icon: 'fas fa-clock', text: 'Late' },
      half_day: { class: 'att-status-halfday', icon: 'fas fa-clock-o', text: 'Half Day' },
      working: { class: 'att-status-working', icon: 'fas fa-play-circle', text: 'Working' }
    };
    return statusConfig[status] || statusConfig.absent;
  };

  const calculateTotalHours = () => {
    return employeeAttendanceData.reduce((total, record) => total + (record.hoursWorked || 0), 0);
  };

  const getPresentCount = () => {
    return employeeAttendanceData.filter(record => record.isPresent).length;
  };

  const getAbsentCount = () => {
    return employeeAttendanceData.filter(record => !record.isPresent).length;
  };

  const getTotalEmployees = () => {
    return allEmployees.length;
  };

  return (
    <div className="att-dashboard-container">
      {/* Header Section */}
      <div className="att-dashboard-header">
        <div className="att-header-content">
          <h2 className="att-dashboard-title">Attendance Dashboard</h2>
          <p className="att-dashboard-subtitle">Monitor and manage daily attendance records</p>
        </div>
        <button className="att-export-btn" onClick={exportAttendance}>
          <i className="fas fa-download"></i>
          <span>Export Data</span>
        </button>
      </div>

      {/* Stats Section */}
      <div className="att-stats-container">
        <div className="att-stat-card">
          <div className="att-stat-icon present">
            <i className="fas fa-user-check"></i>
          </div>
          <div className="att-stat-info">
            <span className="att-stat-number">{getPresentCount()}</span>
            <span className="att-stat-label">Present Today</span>
          </div>
        </div>
        <div className="att-stat-card">
          <div className="att-stat-icon absent">
            <i className="fas fa-user-times"></i>
          </div>
          <div className="att-stat-info">
            <span className="att-stat-number">{getAbsentCount()}</span>
            <span className="att-stat-label">Absent Today</span>
          </div>
        </div>
        <div className="att-stat-card">
          <div className="att-stat-icon total">
            <i className="fas fa-users"></i>
          </div>
          <div className="att-stat-info">
            <span className="att-stat-number">{getTotalEmployees()}</span>
            <span className="att-stat-label">Total Employees</span>
          </div>
        </div>
        <div className="att-stat-card">
          <div className="att-stat-icon rate">
            <i className="fas fa-chart-pie"></i>
          </div>
          <div className="att-stat-info">
            <span className="att-stat-number">
              {getTotalEmployees() > 0 ? Math.round((getPresentCount() / getTotalEmployees()) * 100) : 0}%
            </span>
            <span className="att-stat-label">Attendance Rate</span>
          </div>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="att-nav-container">
        <div className="att-date-nav">
          <button 
            className="att-nav-btn prev" 
            onClick={() => navigateDate('prev')}
            title="Previous Day"
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <div className="att-date-display">
            <button 
              className="att-current-date"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <i className="fas fa-calendar-alt"></i>
              <span className='projectName'>{new Date(selectedDate).toLocaleDateString('en-US', { 
                weekday: 'short', 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })}</span>
              <i className="fas fa-chevron-down"></i>
            </button>

            {showDatePicker && (
              <div className="att-date-picker">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="att-date-input"
                />
              </div>
            )}
          </div>

          <button 
            className="att-nav-btn next" 
            onClick={() => navigateDate('next')}
            title="Next Day"
            disabled={selectedDate === new Date().toISOString().split('T')[0]}
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          <button 
            className="att-today-btn"
            onClick={goToToday}
          >
            <i className="fas fa-home"></i>
            Today
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="att-summary-bar">
        <div className="att-summary-item present">
          <span className="att-summary-label">Present: {getPresentCount()}</span>
          <div className="att-summary-progress">
            <div 
              className="att-summary-fill present"
              style={{ width: `${getTotalEmployees() > 0 ? (getPresentCount() / getTotalEmployees()) * 100 : 0}%` }}
            ></div>
          </div>
        </div>
        <div className="att-summary-item absent">
          <span className="att-summary-label">Absent: {getAbsentCount()}</span>
          <div className="att-summary-progress">
            <div 
              className="att-summary-fill absent"
              style={{ width: `${getTotalEmployees() > 0 ? (getAbsentCount() / getTotalEmployees()) * 100 : 0}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="att-table-container">
        <div className="att-table-wrapper">
          <table className="att-data-table">
            <thead>
              <tr>
                <th className="att-th-employee">Employee</th>
                <th className="att-th-role">Role</th>
                <th className="att-th-date">Date</th>
                <th className="att-th-checkin">Check In</th>
                <th className="att-th-checkout">Check Out</th>
                <th className="att-th-hours">Hours</th>
                <th className="att-th-status">Status</th>
              </tr>
            </thead>
            <tbody>
              {employeeAttendanceData.length > 0 ? (
                employeeAttendanceData.map((record, index) => {
                  const statusInfo = getStatusInfo(record.status);
                  const isAbsent = !record.isPresent;

                  return (
                    <tr key={record.id || index} className={`att-table-row ${isAbsent ? 'absent-row' : 'present-row'}`}>
                      <td className="att-td-employee">
                        <div className="att-employee-info">
                          <div className={`att-employee-avatar ${isAbsent ? 'absent' : 'present'}`}>
                            {record.userName?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="att-employee-details">
                            <span className="att-employee-name">{record.userName}</span>
                            <span className="att-employee-id">ID: {record.userId}</span>
                          </div>
                        </div>
                      </td>
                      <td className="att-td-role">
                        <span className={`att-role-badge ${record.employee?.role || 'employee'}`}>
                          {(record.employee?.role || 'employee').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="att-td-date">
                        <div className="att-date-cell">
                          <i className="fas fa-calendar"></i>
                          {new Date(record.date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="att-td-time">
                        <div className={`att-time-cell ${record.checkIn ? 'checkin' : 'no-checkin'}`}>
                          <i className={`fas ${record.checkIn ? 'fa-sign-in-alt' : 'fa-minus'}`}></i>
                          <span className='projectName'>{record.checkIn || '-'}</span>
                        </div>
                      </td>
                      <td className="att-td-time">
                        <div className={`att-time-cell ${record.checkOut ? 'checkout' : record.checkIn ? 'working' : 'no-checkout'}`}>
                          <i className={`fas ${record.checkOut ? 'fa-sign-out-alt' : record.checkIn ? 'fa-play' : 'fa-minus'}`}></i>
                          <span className='projectName'>
                            {record.checkOut || (record.checkIn ? 'Working' : '-')}
                          </span>
                        </div>
                      </td>
                      <td className="att-td-hours">
                        <div className="att-hours-cell">
                          <div className="att-hours-number">{record.hoursWorked || 0}h</div>
                          {record.hoursWorked > 0 && (
                            <div className="att-hours-bar">
                              <div 
                                className="att-hours-progress"
                                style={{ width: `${Math.min((record.hoursWorked || 0) / 8 * 100, 100)}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="att-td-status">
                        <div className={`att-status-badge ${statusInfo.class}`}>
                          <i className={statusInfo.icon}></i>
                          <span className='projectName'>{statusInfo.text}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="att-no-data-cell">
                    <div className="att-no-data">
                      <div className="att-no-data-icon">
                        <i className="fas fa-users-slash"></i>
                      </div>
                      <div className="att-no-data-content">
                        <h3>No Employees Found</h3>
                        <p>No employee data available in the system</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceView;

// import React, { useState } from 'react';
// import { useData } from '../../contexts/DataContext';
// import './AttendanceDashboard.css';

// const AttendanceView = () => {
//   const { attendance, exportAttendance } = useData();
//   const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
//   const [showDatePicker, setShowDatePicker] = useState(false);

//   // Filter attendance by selected date
//   const filteredAttendance = attendance.filter(record => record.date === selectedDate);

//   // Date navigation functions
//   const navigateDate = (direction) => {
//     const currentDate = new Date(selectedDate);
//     if (direction === 'prev') {
//       currentDate.setDate(currentDate.getDate() - 1);
//     } else if (direction === 'next') {
//       currentDate.setDate(currentDate.getDate() + 1);
//     }
//     setSelectedDate(currentDate.toISOString().split('T')[0]);
//   };

//   const goToToday = () => {
//     setSelectedDate(new Date().toISOString().split('T')[0]);
//   };

//   const handleDateChange = (e) => {
//     setSelectedDate(e.target.value);
//     setShowDatePicker(false);
//   };

//   const getStatusInfo = (status) => {
//     const statusConfig = {
//       present: { class: 'att-status-present', icon: 'fas fa-check-circle', text: 'Present' },
//       absent: { class: 'att-status-absent', icon: 'fas fa-times-circle', text: 'Absent' },
//       late: { class: 'att-status-late', icon: 'fas fa-clock', text: 'Late' },
//       half_day: { class: 'att-status-halfday', icon: 'fas fa-clock-o', text: 'Half Day' }
//     };
//     return statusConfig[status] || statusConfig.present;
//   };

//   const calculateTotalHours = () => {
//     return filteredAttendance.reduce((total, record) => total + (record.hoursWorked || 0), 0);
//   };

//   const getPresentCount = () => {
//     return filteredAttendance.filter(record => record.checkIn).length;
//   };

//   return (
//     <div className="att-dashboard-container">
//       {/* Header Section */}
//       <div className="att-dashboard-header">
//         <div className="att-header-content">
//           <h2 className="att-dashboard-title">Attendance Dashboard</h2>
//           <p className="att-dashboard-subtitle">Monitor and manage daily attendance records</p>
//         </div>
//         <button className="att-export-btn" onClick={exportAttendance}>
//           <i className="fas fa-download"></i>
//           <span>Export Data</span>
//         </button>
//       </div>

//       {/* Stats Section */}
//       <div className="att-stats-container">
//         <div className="att-stat-card">
//           <div className="att-stat-icon present">
//             <i className="fas fa-users"></i>
//           </div>
//           <div className="att-stat-info">
//             <span className="att-stat-number">{getPresentCount()}</span>
//             <span className="att-stat-label">Present Today</span>
//           </div>
//         </div>
//         <div className="att-stat-card">
//           <div className="att-stat-icon total">
//             <i className="fas fa-building"></i>
//           </div>
//           <div className="att-stat-info">
//             <span className="att-stat-number">{filteredAttendance.length}</span>
//             <span className="att-stat-label">Total Records</span>
//           </div>
//         </div>
//         <div className="att-stat-card">
//           <div className="att-stat-icon hours">
//             <i className="fas fa-clock"></i>
//           </div>
//           <div className="att-stat-info">
//             <span className="att-stat-number">{calculateTotalHours().toFixed(1)}h</span>
//             <span className="att-stat-label">Total Hours</span>
//           </div>
//         </div>
//         <div className="att-stat-card">
//           <div className="att-stat-icon rate">
//             <i className="fas fa-chart-line"></i>
//           </div>
//           <div className="att-stat-info">
//             <span className="att-stat-number">
//               {filteredAttendance.length > 0 ? Math.round((getPresentCount() / filteredAttendance.length) * 100) : 0}%
//             </span>
//             <span className="att-stat-label">Attendance Rate</span>
//           </div>
//         </div>
//       </div>

//       {/* Date Navigation */}
//       <div className="att-nav-container">
//         <div className="att-date-nav">
//           <button 
//             className="att-nav-btn prev" 
//             onClick={() => navigateDate('prev')}
//             title="Previous Day"
//           >
//             <i className="fas fa-chevron-left"></i>
//           </button>

//           <div className="att-date-display">
//             <button 
//               className="att-current-date"
//               onClick={() => setShowDatePicker(!showDatePicker)}
//             >
//               <i className="fas fa-calendar-alt"></i>
//               <span className='projectName'>{new Date(selectedDate).toLocaleDateString('en-US', { 
//                 weekday: 'short', 
//                 year: 'numeric', 
//                 month: 'short', 
//                 day: 'numeric' 
//               })}</span>
//               <i className="fas fa-chevron-down"></i>
//             </button>

//             {showDatePicker && (
//               <div className="att-date-picker">
//                 <input
//                   type="date"
//                   value={selectedDate}
//                   onChange={handleDateChange}
//                   className="att-date-input"
//                 />
//               </div>
//             )}
//           </div>

//           <button 
//             className="att-nav-btn next" 
//             onClick={() => navigateDate('next')}
//             title="Next Day"
//             disabled={selectedDate === new Date().toISOString().split('T')[0]}
//           >
//             <i className="fas fa-chevron-right"></i>
//           </button>

//           <button 
//             className="att-today-btn"
//             onClick={goToToday}
//           >
//             <i className="fas fa-home"></i>
//             Today
//           </button>
//         </div>
//       </div>

//       {/* Table Section */}
//       <div className="att-table-container">
//         <div className="att-table-wrapper">
//           <table className="att-data-table">
//             <thead>
//               <tr>
//                 <th className="att-th-employee">Employee</th>
//                 <th className="att-th-date">Date</th>
//                 <th className="att-th-checkin">Check In</th>
//                 <th className="att-th-checkout">Check Out</th>
//                 <th className="att-th-hours">Hours</th>
//                 <th className="att-th-status">Status</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredAttendance.length > 0 ? (
//                 filteredAttendance.map(record => {
//                   const statusInfo = getStatusInfo(record.status);
//                   return (
//                     <tr key={record.id} className="att-table-row">
//                       <td className="att-td-employee">
//                         <div className="att-employee-info">
//                           <div className="att-employee-avatar">
//                             {record.userName?.charAt(0)?.toUpperCase() || 'U'}
//                           </div>
//                           <div className="att-employee-details">
//                             <span className="att-employee-name">{record.userName}</span>
//                             <span className="att-employee-id">ID: {record.userId}</span>
//                           </div>
//                         </div>
//                       </td>
//                       <td className="att-td-date">
//                         <div className="att-date-cell">
//                           <i className="fas fa-calendar"></i>
//                           {new Date(record.date).toLocaleDateString()}
//                         </div>
//                       </td>
//                       <td className="att-td-time">
//                         <div className="att-time-cell checkin">
//                           <i className="fas fa-sign-in-alt"></i>
//                           <span className='projectName'>{record.checkIn || '-'}</span>
//                         </div>
//                       </td>
//                       <td className="att-td-time">
//                         <div className="att-time-cell checkout">
//                           <i className="fas fa-sign-out-alt"></i>
//                           <span className='projectName'>{record.checkOut || 'Working'}</span>
//                         </div>
//                       </td>
//                       <td className="att-td-hours">
//                         <div className="att-hours-cell">
//                           <div className="att-hours-number">{record.hoursWorked || 0}h</div>
//                           <div className="att-hours-bar">
//                             <div 
//                               className="att-hours-progress"
//                               style={{ width: `${Math.min((record.hoursWorked || 0) / 8 * 100, 100)}%` }}
//                             ></div>
//                           </div>
//                         </div>
//                       </td>
//                       <td className="att-td-status">
//                         <div className={`att-status-badge ${statusInfo.class}`}>
//                           <i className={statusInfo.icon}></i>
//                           <span className='projectName'>{statusInfo.text}</span>
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })
//               ) : (
//                 <tr>
//                   <td colSpan="6" className="att-no-data-cell">
//                     <div className="att-no-data">
//                       <div className="att-no-data-icon">
//                         <i className="fas fa-calendar-times"></i>
//                       </div>
//                       <div className="att-no-data-content">
//                         <h3>No Records Found</h3>
//                         <p>No attendance data available for {new Date(selectedDate).toLocaleDateString()}</p>
//                       </div>
//                     </div>
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AttendanceView;